import { useEffect, useMemo, useState, useCallback } from "react";
import { useSnackbar } from "notistack";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import OutgoingMailStatusChip from "../mail/OutgoingMailStatusChip.jsx";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { applyFilterWithAccessorKeys } from "./utils/tableUtils.js";
import { useOutgoingMailActions } from "../../hooks/useOutgoingMailActions.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import PropTypes from "prop-types";

const CATEGORY_LABELS = {
  claim_communication: "Schaden-Kommunikation",
  reminder: "Erinnerung",
  notification: "Benachrichtigung",
  password_reset: "Passwort-Reset",
  two_factor: "2FA-Code",
  user_welcome: "Willkommen",
  system: "System",
  unknown: "Unbekannt",
};

const CATEGORY_COLORS = {
  claim_communication: "primary",
  reminder: "info",
  notification: "default",
  password_reset: "error",
  two_factor: "warning",
  user_welcome: "success",
  system: "default",
  unknown: "default",
};

export default function OutgoingMailsTable({
  category = "all",
  onRowClick = () => {},
  onCategoryChange = () => {},
  selectedMailId = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [categoryCounts, setCategoryCounts] = useState(null);

  const fetchCategoryCounts = useCallback(async () => {
    const params = {
      api_version: "v2",
    };

    const response = await backendGet("/data/outgoing-mails/folder-counts", {
      label: "Ausgehende E-Mail Übersicht",
      enqueueSnackbar,
      options: {
        params,
      },
    });

    if (response?.data) {
      setCategoryCounts(response.data);
    }
  }, [enqueueSnackbar]);

  const { state: mailsState, setState: setMailsState } =
    useOutgoingMailActions(fetchCategoryCounts);

  const tableKey = "outgoing-mails";
  const storageKey = "v2_table_outgoing_mails";
  const defaultRowsPerPage = getUserRowsPerPage(user, 50);

  const tablesConfig = useMemo(() => {
    const filters = {};

    if (category !== "all") {
      filters.category = category;
    }

    const extraParams = {
      sort: [{ field: "sent_at", order: "desc" }],
      include: "attachments,source",
    };

    if (Object.keys(filters).length > 0) {
      extraParams.filters = filters;
    }

    return [
      {
        key: tableKey,
        label: "Ausgehende E-Mails",
        route: `/data/outgoing-mails`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams,
        onStateChange: setMailsState,
      },
    ];
  }, [defaultRowsPerPage, storageKey, category]);

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const rows = mailsState?.data?.data || [];

  useEffect(() => {
    fetchCategoryCounts();
  }, [fetchCategoryCounts]);

  const filterTabs = useMemo(
    () => [
      {
        key: "all",
        label: "Alle",
        count: categoryCounts?.all ?? tableState.totalRows ?? 0,
        onClick: onCategoryChange,
      },
      {
        key: "claim_communication",
        label: "Schaden",
        count: categoryCounts?.claim_communication ?? 0,
        onClick: onCategoryChange,
      },
      {
        key: "reminder",
        label: "Erinnerungen",
        count: categoryCounts?.reminder ?? 0,
        onClick: onCategoryChange,
      },
      {
        key: "notification",
        label: "Benachrichtigungen",
        count: categoryCounts?.notification ?? 0,
        onClick: onCategoryChange,
      },
      {
        key: "unknown",
        label: "Unbekannt",
        count: categoryCounts?.unknown ?? 0,
        onClick: onCategoryChange,
      },
      ...(categoryCounts?.system_all !== undefined
        ? [
            {
              key: "system_all",
              label: "System",
              count: categoryCounts.system_all,
              onClick: onCategoryChange,
            },
          ]
        : []),
    ],
    [categoryCounts, tableState.totalRows, onCategoryChange]
  );

  const formatRecipients = (to) => {
    if (!to || !Array.isArray(to) || to.length === 0) {
      return "-";
    }
    const first = to[0];
    const formatRecipient = (r) => {
      const email = typeof r === "string" ? r : r.email;
      const name = typeof r === "object" ? r.name : null;
      return name ? `${name} <${email}>` : email;
    };
    const display = formatRecipient(first);
    if (to.length > 1) {
      return (
        <Tooltip title={to.map(formatRecipient).join(", ")}>
          {display} +{to.length - 1}
        </Tooltip>
      );
    }
    return display;
  };

  const columns = [
    {
      accessorKey: "subject",
      header: "Betreff",
      size: 350,
      Cell: ({ row }) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
            }}
          >
            {row.original.subject}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "to",
      header: "Empfänger",
      size: 250,
      Cell: ({ row }) => formatRecipients(row.original.to),
    },
    {
      accessorKey: "category",
      header: "Kategorie",
      size: 150,
      Cell: ({ row }) => {
        const category = row.original.category || "unknown";
        return (
          <Chip
            label={CATEGORY_LABELS[category] || category}
            color={CATEGORY_COLORS[category] || "default"}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      Cell: ({ row }) => (
        <OutgoingMailStatusChip status={row.original.status} />
      ),
    },
    {
      accessorKey: "sent_at",
      header: "Gesendet",
      size: 150,
      Cell: ({ cell }) =>
        cell.getValue()
          ? new Date(cell.getValue()).toLocaleString("de-DE")
          : "-",
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) =>
        updateTableState(tableKey, {
          filters: applyFilterWithAccessorKeys(filters, columns),
        })
      }
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      enableTopToolbar
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => onRowClick(row.original),
        sx: {
          cursor: "pointer",
          backgroundColor:
            row.original.id === selectedMailId ? "#F1F1F199" : "#fff",
        },
      })}
      filterTabs={filterTabs}
      activeFilterTab={category}
      enableStickyHeader
    />
  );
}

OutgoingMailsTable.propTypes = {
  category: PropTypes.string,
  onRowClick: PropTypes.func,
  onCategoryChange: PropTypes.func,
  selectedMailId: PropTypes.string,
};
